import * as THREE from 'three';

export interface RendererOptions {
  canvas: HTMLCanvasElement;
  maxPixelRatio?: number;
}

export class Renderer {
  readonly gl: THREE.WebGLRenderer;
  readonly canvas: HTMLCanvasElement;
  private readonly maxPixelRatio: number;
  private paused = false;

  constructor(opts: RendererOptions) {
    this.canvas = opts.canvas;
    // Cap at 1 — the liquid is a soft blurry effect and renders identically
    // to the eye at CSS pixel resolution; on a retina display that's a 4x
    // reduction in fragment work for the heavy FBM shader.
    this.maxPixelRatio = opts.maxPixelRatio ?? 1;
    this.gl = new THREE.WebGLRenderer({
      canvas: this.canvas,
      antialias: false,
      alpha: false,
      powerPreference: 'high-performance',
      stencil: false,
      depth: true,
    });
    this.gl.setClearColor(0x000000, 1);
    this.applySize();

    window.addEventListener('resize', this.applySize);
    document.addEventListener('visibilitychange', this.onVisibility);
  }

  isPaused(): boolean {
    return this.paused;
  }

  size(): { width: number; height: number } {
    return {
      width: this.canvas.clientWidth,
      height: this.canvas.clientHeight,
    };
  }

  dispose(): void {
    window.removeEventListener('resize', this.applySize);
    document.removeEventListener('visibilitychange', this.onVisibility);
    this.gl.dispose();
  }

  private applySize = (): void => {
    const w = window.innerWidth;
    const h = window.innerHeight;
    const dpr = Math.min(window.devicePixelRatio, this.maxPixelRatio);
    this.gl.setPixelRatio(dpr);
    this.gl.setSize(w, h, false);
  };

  private onVisibility = (): void => {
    this.paused = document.hidden;
  };
}
